import { useEffect, useMemo, useState } from 'react';
import { Search, Download, Copy, Check, FileSpreadsheet } from 'lucide-react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { adminListWaitlistSignups, type WaitlistSignupRecord } from '@shared/services/admin-api';

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminWaitlist() {
  const [signups, setSignups] = useState<WaitlistSignupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { signups } = await adminListWaitlistSignups();
        if (!cancelled) setSignups(signups);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load waitlist');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return signups;
    const q = search.trim().toLowerCase();
    return signups.filter(
      (s) =>
        s.email.toLowerCase().includes(q) ||
        s.source?.toLowerCase().includes(q) ||
        s.referrer?.toLowerCase().includes(q),
    );
  }, [signups, search]);

  function downloadCsv() {
    const header = ['email', 'source', 'createdAt', 'referrer', 'userAgent'];
    const rows = filtered.map((s) => [
      s.email,
      s.source ?? '',
      s.createdAt ? new Date(s.createdAt).toISOString() : '',
      s.referrer ?? '',
      s.userAgent ?? '',
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => {
            const v = String(cell);
            return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
          })
          .join(','),
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadXlsx() {
    // Lazy-load SheetJS so the ~700KB library only ships when an admin
    // actually clicks the button.
    const XLSX = await import('xlsx');
    const rows = filtered.map((s) => ({
      email: s.email,
      source: s.source ?? '',
      joined: s.createdAt ? new Date(s.createdAt).toISOString() : '',
      referrer: s.referrer ?? '',
      userAgent: s.userAgent ?? '',
    }));
    const sheet = XLSX.utils.json_to_sheet(rows, {
      header: ['email', 'source', 'joined', 'referrer', 'userAgent'],
    });
    // Reasonable default column widths so the file opens nicely in Excel.
    sheet['!cols'] = [
      { wch: 32 }, // email
      { wch: 14 }, // source
      { wch: 22 }, // joined
      { wch: 28 }, // referrer
      { wch: 48 }, // userAgent
    ];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Waitlist');
    XLSX.writeFile(book, `waitlist-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  async function copyEmails() {
    const text = filtered.map((s) => s.email).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl font-bold mb-1">Waitlist</h1>
              <p className="text-content-muted text-sm">
                {loading ? 'Loading…' : `${signups.length} total signup${signups.length === 1 ? '' : 's'}`}
                {!loading && search && ` · ${filtered.length} match${filtered.length === 1 ? '' : 'es'}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyEmails}
                disabled={loading || filtered.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm font-body text-content-secondary hover:text-content hover:border-indigo-400/40 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy emails'}
              </button>
              <button
                onClick={downloadCsv}
                disabled={loading || filtered.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-dark-border bg-dark-surface px-3 py-2 text-sm font-body text-content-secondary hover:text-content hover:border-indigo-400/40 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={14} />
                CSV
              </button>
              <button
                onClick={downloadXlsx}
                disabled={loading || filtered.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-[#7E70EF] text-white px-3 py-2 text-sm font-body font-semibold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileSpreadsheet size={14} />
                Excel
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg p-3 mb-4 text-sm bg-error/10 border border-error/30 text-error">
              {error}
            </div>
          )}

          <div className="relative max-w-md mb-5">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email, source, referrer…"
              className="w-full rounded-lg text-sm pl-9 pr-3 py-2.5 bg-dark-surface border border-dark-border text-content focus:border-indigo-500 focus:outline-none"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-dark-surface border border-dark-border p-12 text-center">
              <div className="text-sm font-body text-content-secondary mb-1">
                {signups.length === 0 ? 'No waitlist signups yet' : 'No matches'}
              </div>
              {signups.length === 0 && (
                <p className="text-xs text-content-muted">
                  Signups submitted from the Coming Soon holding page will appear here.
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-dark-surface border border-dark-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold">Email</th>
                      <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold">Source</th>
                      <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold">Joined</th>
                      <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-content-muted font-semibold">Referrer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => (
                      <tr key={s.id} className="border-b border-dark-border last:border-b-0 hover:bg-dark-hover transition-colors">
                        <td className="px-4 py-3 font-body text-content">{s.email}</td>
                        <td className="px-4 py-3 font-body text-content-secondary">{s.source ?? '—'}</td>
                        <td className="px-4 py-3 font-body text-content-secondary">{formatDate(s.createdAt)}</td>
                        <td className="px-4 py-3 font-body text-content-muted truncate max-w-[20rem]" title={s.referrer ?? ''}>
                          {s.referrer || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
